import { useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { InventoryProvider } from '@scaleaq/sitecontrol-lib';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledSelectField } from '@/components/inputFields/ControlledSelectField';
import {
  useRegisterWidgetSitePanelSave,
  useWidgetSitePanelCtx,
  useWidgetSiteTabbedShellCtx,
} from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { useGetSensorsAndAncestors } from '@/dataAccess/rest';
import { useLayoutProps } from '@/hooks';
import { groupSensorOptionsByType, responsive } from '@/utils';

import { useSiteContext } from '../../../../providers';
import { BiomassDistributionConfig } from '../../biomassDistribution.types.ts';

type BiomassDistributionFormProps = {
  rootId?: string;
};

export const BiomassDistributionForm = ({ rootId }: BiomassDistributionFormProps) => {
  const { t } = useTranslation(['common', 'orbitOne']);
  const { onSave } = useWidgetSitePanelCtx();
  const { shellWidgetId } = useWidgetSiteTabbedShellCtx();
  const { currentSite: { id: inventoryId } = {} } = useSiteContext();

  const { config, update } = useLayoutProps<BiomassDistributionConfig | undefined>({
    widgetId: shellWidgetId,
    defaultConfig: undefined,
  });

  const {
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<BiomassDistributionConfig>({
    defaultValues: { ...config },
  });

  const { data: sensors = [], isLoading: isLoadingSensors } = useGetSensorsAndAncestors({
    provider: InventoryProvider.Biomass,
    inventoryId,
  });

  const sensorNames = useMemo(
    () => Object.fromEntries(sensors.map(sensor => [sensor.id, sensor.name])),
    [sensors]
  );

  const save = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const newConfig = getValues();
    const sensorName = sensorNames[newConfig.sensorId] ?? '';

    update({
      widgetId: shellWidgetId,
      config: { ...newConfig, sensorName },
    });
    onSave({ ...newConfig, sensorName });
  };

  useRegisterWidgetSitePanelSave(save);

  const sensorOptions = useMemo(
    () =>
      groupSensorOptionsByType(
        sensors.filter(sensor => (!rootId ? true : sensor.path.indexOf(rootId) !== -1)),
        sensor => sensor.id
      ),
    [rootId, sensors]
  );

  return (
    <FlexAligner direction={'column'} gap={responsive.spacing['5xlarge']}>
      <ControlledSelectField
        name={'sensorId'}
        control={control}
        label={t(`common:availableSensors`)}
        placeholder={t('common:selectSensor')}
        options={sensorOptions}
        rules={{
          required: t('orbitOne:biomassDistribution.config.isRequired.sensor'),
        }}
        caution={errors.sensorId?.message}
        isLoading={isLoadingSensors}
      />
    </FlexAligner>
  );
};
